import React, { useContext, useState } from 'react'

import {
  Icon,
  Input,
  Menu,
  Grid,
  Header,
  Feed,
  Image,
  Rating,
  DropdownItemProps,
  Dimmer,
  Loader,
  Pagination,
  PaginationProps,
} from 'semantic-ui-react'
import { Confirm, Modal, Form, Button, Dropdown, List, Label } from 'semantic-ui-react'
import { InputOnChangeData, CheckboxProps } from 'semantic-ui-react'

import threeDotsIcon from '../../../_images/3-dots.svg'
import Data, { Comment, ExtendedClipAnswer, IClip, IClipGroup, IClipIntegrity, UpdateOptions } from '../../../api/Data'
import { maxLength } from '../../../config/constants'
import { GlobalContext } from '../../../contexts/GlobalContext'
import { UserContext } from '../../../contexts/UserContext'
import moment from '../../../utils/Moment'
import { downloadFileFromBrowser } from '../../../utils/sharedFunctions'
import { downloadFreshSignedMedia } from '../../../utils/signedMedia'
import useFreshMediaUrl from '../../../utils/useFreshMediaUrl'

import CustomLabel from '../../../components/common/custom-label/CustomLabel'
import ExternalLink from '../../../components/common/ExternalLink'
import UpgradeTag from '../../../components/common/UpgradeTag'
import LocationSearchField from '../../../components/modals/LocationSearchField'

import './CurateClipModal.scss'
import ClipIntegrityPanel from './ClipIntegrityPanel'
import GalleryClipTrimModal from './CurateClipTrimModal'

const GalleryClipModal = (props: IProps) => {
  const { isMobile, dispatchGlobalContext } = useContext(GlobalContext)
  const { userProfile } = useContext(UserContext)

  // Props
  const { clip: clipData, clipGroup, displaySetting, loading, updateClip, deleteClip, setShowModal } = props

  // States
  const [clip, setClip] = useState(clipData)
  const clipId = clip.id
  const [tagValue, setTagValue] = useState('')
  const [activeVideoTab, setActiveVideoTab] = useState('view')
  const [activeFormTab, setActiveFormTab] = useState('contributors')
  const [comments, setComments] = useState<Comment[]>([])
  const [commentValue, setCommentValue] = useState('')
  const [isAnswersLoading, setIsAnswersLoading] = useState(false)
  const [answers, setAnswers] = useState<ExtendedClipAnswer[]>([])
  const [integrity, setIntegrity] = useState<IClipIntegrity | null>(null)
  const [isIntegrityLoading, setIsIntegrityLoading] = useState(false)
  const [integrityError, setIntegrityError] = useState(false)
  const [isIntegrityProcessing, setIsIntegrityProcessing] = useState(false)
  const [isIntegrityAnalysisRequested, setIsIntegrityAnalysisRequested] = useState(false)

  const [isDownloading, setIsDownloading] = useState(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [showReencodeConfirmationModal, setShowReencodeConfirmationModal] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)

  const requestPlaybackUrl = React.useCallback(() => Data.downloadClip(clipId, '480'), [clipId])
  const { mediaUrl, isMediaLoading, mediaError, videoRef, handleMediaError, handleMediaLoaded, retryMedia } =
    useFreshMediaUrl(clipId, requestPlaybackUrl)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement> | null, data: InputOnChangeData) => {
    const { name, value } = data

    setClip({ ...clip, [name]: value })
  }

  const handleDelete = () => {
    setShowConfirmationModal(true)
  }

  const handleReencode = () => {
    setShowReencodeConfirmationModal(false)
    updateClip(clip, { reloadPage: true, skipEncoding: false })
  }

  // Update state once the user picks up a place and location
  const setLocation = (
    indexToUpdate: number,
    suggestion: { place: string; location: string; lat: number; lng: number }
  ) => {
    const { place, location, lat, lng } = suggestion
    setClip({ ...clip, place, location, lat1: lat, lon1: lng })
  }

  // Reset location
  const resetLocation = () => {
    setClip({ ...clip, place: '', location: '', lat1: null, lon1: null })
  }

  // Add tag
  // const addTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
  const addTag = async (e: any) => {
    // Remove comma from tag value
    const valueSanitized = e.target.value?.replace(/,/g, ' ')
    if (e.key !== 'Enter' || !valueSanitized || clip.tags.includes(valueSanitized)) return

    const updatedClip = {
      ...clip,
      tags: [...clip.tags, valueSanitized],
    }

    setClip(updatedClip)
    setTagValue('')
    setIsUpdating(true)

    try {
      await updateClip(updatedClip, { reloadPage: false, skipEncoding: true })
    } catch (error) {
      alert('An error occurred while adding the tag. Please try again.')
    } finally {
      setIsUpdating(false)
    }
  }

  // Remove tag
  const removeTag = async (value: string) => {
    if (isUpdating) return

    const newTagArr = clip.tags.filter((tag) => tag !== value)

    const updatedClip = {
      ...clip,
      tags: newTagArr,
    }

    setClip(updatedClip)
    setIsUpdating(true)

    try {
      await updateClip(updatedClip, { reloadPage: false, skipEncoding: true })
    } catch (error) {
      alert('An error occurred while removing the tag. Please try again.')
      setClip(clip)
    } finally {
      setIsUpdating(false)
    }
  }

  // Feature clip
  const handleFeatureClip = (e: React.MouseEvent<HTMLInputElement, MouseEvent>, data: CheckboxProps) => {
    const { checked } = data
    setClip((prevState) => ({ ...prevState, isFeatured: Boolean(checked) }))
  }

  const addComment = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && commentValue.trim()) {
      const newComment: Comment = {
        id: new Date().toISOString(),
        comment: commentValue,
        collaboratorName: userProfile.username || 'unknown user',
      }
      const updatedClip = {
        ...clip,
        comments: [...clip.comments, newComment],
      }

      setClip(updatedClip)
      setComments([...comments, newComment])
      setCommentValue('')
      setIsUpdating(true)
      try {
        await updateClip(updatedClip, { reloadPage: false, skipEncoding: true })
      } catch (error) {
        alert('An error occurred while adding the comment. Please try again.')
      } finally {
        setIsUpdating(false)
      }
    }
  }

  React.useEffect(() => {
    if (!clipData) return

    const getAnswersForClip = async () => {
      setIsAnswersLoading(true)
      const { data, error } = await Data.getClipAnswers(clipData.id)
      setIsAnswersLoading(false)

      if (error) {
        console.error(`Unable to load clip answers for clip #${clipData.id}`, error)

        setAnswers([])
        return
      }

      setAnswers(data || [])
    }

    getAnswersForClip()

    if (clipData.comments) setComments(clipData.comments)
  }, [clipData])

  React.useEffect(() => {
    setIsIntegrityAnalysisRequested(false)
  }, [clipId])

  React.useEffect(() => {
    if (activeFormTab !== 'integrity') return

    let isCurrent = true
    const getIntegrityForClip = async () => {
      setIsIntegrityLoading(true)
      setIntegrityError(false)
      const { data, error } = await Data.getClipIntegrity(clipId)

      if (!isCurrent) return
      setIsIntegrityLoading(false)
      setIntegrityError(Boolean(error))
      setIntegrity(error ? null : data)
      if (!error && data?.available) setIsIntegrityAnalysisRequested(false)
    }

    getIntegrityForClip()

    return () => {
      isCurrent = false
    }
  }, [activeFormTab, clipId])

  const processIntegrity = async () => {
    if (isIntegrityProcessing) return

    setIsIntegrityProcessing(true)
    setIntegrityError(false)
    const { error } = await Data.processClipIntegrity(clipId)
    if (error) {
      setIsIntegrityProcessing(false)
      setIsIntegrityAnalysisRequested(false)
      setIntegrityError(true)
      return
    }

    setIsIntegrityAnalysisRequested(true)

    const maxAttempts = 20
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      await new Promise((resolve) => setTimeout(resolve, 3000))
      const result = await Data.getClipIntegrity(clipId)
      if (!result.error && result.data?.available) {
        setIntegrity(result.data)
        setIsIntegrityProcessing(false)
        setIsIntegrityAnalysisRequested(false)
        return
      }
    }

    setIsIntegrityProcessing(false)
  }

  const handleDownloadClip = async (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    dropDownData: DropdownItemProps
  ) => {
    const type = String(dropDownData.value)

    setIsDownloading(true)

    try {
      const download = await downloadFreshSignedMedia(() => Data.downloadClip(clip.id, type))

      downloadFileFromBrowser({ data: download.data, type: 'mp4', fileName: download.fileName })
    } catch (error) {
      if (error?.response?.status === 403) {
        setIsDownloading(false)
        return dispatchGlobalContext({ type: 'show update plan modal' })
      }
      alert('An error occurred')
    }

    setIsDownloading(false)
  }

  if (!clip) return null

  const { contributor, createdAt, isFeatured, tags, question } = clip
  const { filmingLocalTime, rating } = clip
  const clipWithCompliance = clip as IClip & {
    consentId?: string | null
    contentControlStatus?: number | null
    contentControlReason?: string | null
    contentControlUpdatedAt?: string | null
    contentControlEvents?: unknown[] | null
  }
  const contentControlStatus = Number(clipWithCompliance.contentControlStatus || 1)
  const contentControlLabel =
    contentControlStatus === 2 ? 'Flagged for review' : contentControlStatus === 3 ? 'Hidden from gallery' : ''

  const { videoResolution, cleanClip, rawClip } = userProfile.plan

  // Is user collaborator
  const isUserCollaborator = (): boolean => {
    return userProfile.userAccessRights === 'collaborator'
  }

  const handleClipGroupPageChange = (_: React.MouseEvent, data: PaginationProps) => {
    const { activePage } = data

    if (typeof activePage === 'number') {
      const targetClip = clipGroup!.clips[activePage - 1]

      setClip(targetClip)
      setShowModal(targetClip)
    }
  }
  const updateContentControlStatus = async (status: number, reason: string) => {
    const updatedClip = {
      ...clip,
      contentControlStatus: status,
      contentControlReason: reason,
      contentControlUpdatedAt: new Date().toISOString(),
      contentControlEvents: [
        ...((clipWithCompliance.contentControlEvents as unknown[]) || []),
        {
          type: status === 3 ? 'hidden' : 'visible',
          reason,
          createdAt: new Date().toISOString(),
        },
      ],
    } as IClip

    setClip(updatedClip)
    setIsUpdating(true)

    try {
      await updateClip(updatedClip, { reloadPage: true, skipEncoding: true })
    } catch (error) {
      alert('An error occurred while updating Content Control. Please try again.')
      setClip(clip)
    } finally {
      setIsUpdating(false)
    }
  }
  // #region Render functions
  const renderClipAnswers = () => {
    if (!answers) return null

    return answers.map((a) => {
      const linkedValuesNodes = a.datasetLinkedValues?.map((lv) => (
        <>
          <label className="contributor-input__label">{lv.columnName}</label>
          <Form.Input
            className="contributor-input__input"
            fluid
            inverted
            readOnly
            name={`answer_${a.id}_${lv.columnName}`}
            value={lv.value}
          />
        </>
      ))

      return (
        <Form.Field className="contributor-input__container">
          <label className="contributor-input__label">{a.questionText}</label>
          <Form.Input
            className="contributor-input__input"
            fluid
            inverted
            readOnly
            name={`answer_${a.id}`}
            value={a.answerText}
          />

          {linkedValuesNodes && <Form.Field className="contributor-input__container">{linkedValuesNodes}</Form.Field>}
        </Form.Field>
      )
    })
  }

  const renderClipGroupPagination = () => {
    if (!clipGroup || !clipGroup.clips.length) return null

    const activePage = clipGroup.clips.findIndex((c) => c.id === clip.id) + 1

    return (
      <Grid.Row>
        <Grid.Column textAlign="center">
          <Pagination
            activePage={activePage}
            totalPages={clipGroup.clips.length}
            size="mini"
            firstItem={null}
            lastItem={null}
            onPageChange={handleClipGroupPageChange}
          />
        </Grid.Column>
      </Grid.Row>
    )
  }
  // #endregion

  return (
    <Modal className="curate-clip-modal" dimmer="blurring" open closeIcon onClose={() => setShowModal(null)}>
      <Grid
        stackable
        className={isMobile ? 'mobile' : ''}
        style={{ margin: '1rem', height: isMobile ? 'auto' : '650px' }}
      >
        <Grid.Column width={7} textAlign="center" style={{ backgroundColor: '#f5f5f6', borderRadius: '0.5rem' }}>
          <Grid>
            <Grid.Row>
              <Grid.Column width={16} textAlign="center">
                <Button.Group>
                  <Button primary={activeVideoTab === 'view'} onClick={() => setActiveVideoTab('view')}>
                    View
                  </Button>
                  <Button primary={activeVideoTab === 'trim'} onClick={() => setActiveVideoTab('trim')}>
                    Trim
                  </Button>
                </Button.Group>
              </Grid.Column>
            </Grid.Row>
            {activeVideoTab === 'trim' && isMediaLoading && (
              <Grid.Row>
                <Grid.Column>
                  <Loader active inline="centered" content="Loading video..." />
                </Grid.Column>
              </Grid.Row>
            )}
            {activeVideoTab === 'trim' && mediaError && !isMediaLoading && (
              <Grid.Row>
                <Grid.Column>
                  <Button icon="refresh" content="Reload video" onClick={retryMedia} />
                </Grid.Column>
              </Grid.Row>
            )}
            {activeVideoTab === 'trim' && mediaUrl && !isMediaLoading && !mediaError && (
              <GalleryClipTrimModal clip={clip} videoUrl={mediaUrl} onMediaError={handleMediaError} />
            )}
            {activeVideoTab === 'view' && (
              <>
                <Grid.Row>
                  <Grid.Column>
                    {isMediaLoading && <Loader active inline="centered" content="Loading video..." />}
                    {mediaError && !isMediaLoading && (
                      <Button icon="refresh" content="Reload video" onClick={retryMedia} />
                    )}
                    {mediaUrl && !isMediaLoading && !mediaError && (
                      // eslint-disable-next-line jsx-a11y/media-has-caption
                      <video
                        key={mediaUrl}
                        ref={videoRef}
                        controls
                        autoPlay
                        tabIndex={-1}
                        onError={handleMediaError}
                        onLoadedMetadata={handleMediaLoaded}
                        style={{ width: '100%', height: 'auto', borderRadius: '0.5rem' }}
                      >
                        <source src={mediaUrl} />
                      </video>
                    )}
                  </Grid.Column>
                </Grid.Row>

                {renderClipGroupPagination()}

                {(clipWithCompliance.consentId || contentControlLabel) && (
                  <Grid.Row>
                    <Grid.Column textAlign="center">
                      {clipWithCompliance.consentId && (
                        <Label color="green" basic>
                          © Consent granted
                        </Label>
                      )}
                      {contentControlLabel && (
                        <Label color={contentControlStatus === 2 ? 'yellow' : 'grey'} basic>
                          {contentControlLabel}
                        </Label>
                      )}
                    </Grid.Column>
                  </Grid.Row>
                )}

                <Grid.Row>
                  <Grid.Column textAlign="center">
                    <Feed>
                      <Feed.Event>
                        <Feed.Content style={{ textAlign: 'center' }}>
                          <Feed.Date>Date uploaded</Feed.Date>
                          <Feed.Summary>{moment(createdAt).format('ll')}</Feed.Summary>
                        </Feed.Content>
                      </Feed.Event>
                    </Feed>
                    <Dropdown text="Download video" button className="secondary" loading={isDownloading}>
                      <Dropdown.Menu style={{ width: '230px' }}>
                        <Dropdown.Item key="1080" value="1080" onClick={handleDownloadClip}>
                          <Header as="h6">
                            HD video (1920x1080)
                            {videoResolution < 2 && (
                              <UpgradeTag
                                text="Upgrade your plan to download this video in full HD"
                                style={{ float: 'right' }}
                              />
                            )}
                          </Header>
                        </Dropdown.Item>
                        <Dropdown.Item key="480" value="480" onClick={handleDownloadClip}>
                          <Header as="h6">SD video (854x480)</Header>
                        </Dropdown.Item>
                        {/* <Dropdown.Divider /> */}
                        <Dropdown.Item key="clean" value="clean" onClick={handleDownloadClip}>
                          <Header as="h6">
                            Clean video (1920x1080)
                            {!cleanClip && (
                              <UpgradeTag
                                text="Upgrade your plan to download this video in clean format"
                                style={{ float: 'right' }}
                              />
                            )}
                          </Header>
                        </Dropdown.Item>
                        <Dropdown.Item key="raw" value="raw" onClick={handleDownloadClip}>
                          <Header as="h6">
                            Raw video (original)
                            {!rawClip && (
                              <UpgradeTag
                                text="Upgrade your plan to download this video in raw format"
                                style={{ float: 'right' }}
                              />
                            )}
                          </Header>
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column textAlign="center">
                    If you encounter sound playback issues using QuickTime Player please try opening the video in
                    another video player.{' '}
                    <ExternalLink url="https://vloggi.com/docs/quick-help-guide-resolving-known-vloggi-video-sound-issues-in-quicktime-player/">
                      Read more
                    </ExternalLink>
                  </Grid.Column>
                </Grid.Row>
              </>
            )}
          </Grid>
        </Grid.Column>
        <Grid.Column width={9} stretched>
          <Grid id="clipInformation">
            <Grid.Row>
              <Grid.Column width={16}>
                <div style={{ marginBottom: isMobile ? '1rem' : undefined }}>
                  <Header as="h2">Clip information</Header>
                  <Menu pointing secondary className="clip-information-tabs">
                    <Menu.Item
                      active={activeFormTab === 'contributors'}
                      onClick={() => setActiveFormTab('contributors')}
                    >
                      Contributor data
                    </Menu.Item>
                    <Menu.Item active={activeFormTab === 'additional'} onClick={() => setActiveFormTab('additional')}>
                      Collaboration
                    </Menu.Item>
                    <Menu.Item active={activeFormTab === 'integrity'} onClick={() => setActiveFormTab('integrity')}>
                      Device data
                    </Menu.Item>
                  </Menu>
                </div>
                {activeFormTab === 'contributors' && (
                  <Form>
                    {/* Feature clip */}
                    <Form.Field>
                      <p style={{ color: '#666666', marginTop: 0 }}>
                        Set an example video from your entries for contributors to see on the upload page.
                      </p>
                      <CustomLabel>
                        <CustomLabel.Text text="Feature clip" />
                        <CustomLabel.Popup popupContent="You can feature a clip to appear on your contributor page. This helps contributors understand the type of video you are expecting from them."></CustomLabel.Popup>
                        <CustomLabel.Checkbox
                          name="isFeatured"
                          checked={isFeatured}
                          onClick={handleFeatureClip}
                        ></CustomLabel.Checkbox>
                      </CustomLabel>
                    </Form.Field>

                    {/* Contributor name field */}
                    <Form.Field className="contributor-input__container">
                      <label className="contributor-input__label">Name</label>
                      <Form.Input
                        className="contributor-input__input"
                        fluid
                        inverted
                        name="contributor"
                        maxLength={maxLength.contributor}
                        value={contributor || ''}
                        onChange={handleChange}
                        disabled={isUserCollaborator()}
                      />
                    </Form.Field>
                    {/* Associated question */}
                    {question && (
                      <Form.Field className="contributor-input__container">
                        <label className="contributor-input__label">Question asked</label>
                        <Form.Input
                          className="contributor-input__input"
                          fluid
                          inverted
                          name="question"
                          maxLength={maxLength.question}
                          value={question || ''}
                          disabled={true}
                        />
                      </Form.Field>
                    )}
                    {/* Rating */}
                    {displaySetting.showRating && (
                      <Form.Field className="contributor-input__container">
                        <label className="contributor-input__label">{displaySetting.ratingPrompt}</label>
                        <div style={{ margin: '1rem 0' }}>
                          <Rating
                            icon="star"
                            defaultRating={rating}
                            maxRating={5}
                            size="massive"
                            name="rating"
                            onRate={(e, { name, rating: value }) => handleChange(null, { name, value: String(value) })}
                            disabled={isUserCollaborator()}
                          />
                        </div>
                      </Form.Field>
                    )}
                    {/* Location Field */}
                    {displaySetting.showLocation && (
                      <LocationSearchField
                        index={1}
                        upload={clip}
                        setLocation={setLocation}
                        resetLocation={resetLocation}
                      />
                    )}

                    {isAnswersLoading ? <Loader size="small">Loading clip answers...</Loader> : renderClipAnswers()}
                  </Form>
                )}
                {activeFormTab === 'additional' && (
                  <div style={{ position: 'relative' }}>
                    <Dimmer active={isUpdating} inverted>
                      <Loader>Loading</Loader>
                    </Dimmer>
                    <Form>
                      {/* Tags */}
                      <Form.Field>
                        <label>Tags</label>
                        <List horizontal>
                          {tags.map((tag) => (
                            <List.Item key={tag}>
                              <Label>
                                {tag}
                                <Icon name="delete" onClick={() => removeTag(tag)} />
                              </Label>
                            </List.Item>
                          ))}
                          <List.Item>
                            <Input
                              size="mini"
                              style={{ width: '100%' }}
                              name="tag"
                              value={tagValue}
                              onKeyDown={addTag}
                              onChange={(e, { value }) => setTagValue(value)}
                            />
                          </List.Item>
                        </List>
                      </Form.Field>
                      {/* Comments Section */}
                      <Form.Field>
                        <label>Comments</label>
                        <List style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {clip.comments.map((comment) => (
                            <List.Item key={comment.id}>
                              <div className="ui segment">
                                <p>
                                  <strong>{comment.collaboratorName}</strong>{' '}
                                  {moment(comment.id).format('D MMMM YYYY [at] HH:mm')}
                                </p>
                                <p>{comment.comment}</p>
                              </div>
                            </List.Item>
                          ))}
                        </List>
                        <Input
                          size="mini"
                          style={{ width: '100%' }}
                          name="comment"
                          value={commentValue}
                          onChange={(e, { value }) => setCommentValue(value)}
                          onKeyDown={addComment}
                          placeholder="Add a comment"
                        />
                      </Form.Field>
                    </Form>
                  </div>
                )}
                {activeFormTab === 'integrity' && (
                  <ClipIntegrityPanel
                    data={integrity}
                    loading={isIntegrityLoading}
                    error={integrityError}
                    analysisRequested={isIntegrityAnalysisRequested || isIntegrityProcessing}
                    recordedAt={filmingLocalTime}
                    onProcess={processIntegrity}
                  />
                )}
              </Grid.Column>
            </Grid.Row>

            <Grid.Row textAlign="center" verticalAlign="bottom">
              <Grid.Column width={2} floated="right"></Grid.Column>
              <Grid.Column width={12}>
                <Button onClick={() => setShowModal(null)}>Close</Button>
                <Button
                  primary
                  loading={loading}
                  onClick={() => updateClip(clip, { reloadPage: false, skipEncoding: true })}
                  disabled={isUpdating}
                >
                  Save
                </Button>
              </Grid.Column>
              <Grid.Column width={2}>
                <Dropdown
                  trigger={<Image style={{ width: '1.5rem' }} src={threeDotsIcon} />}
                  pointing="top left"
                  icon={null}
                >
                  <Dropdown.Menu>
                    {contentControlStatus === 3 ? (
                      <Dropdown.Item
                        text="Restore to gallery"
                        onClick={() => updateContentControlStatus(1, 'Restored to public gallery')}
                      />
                    ) : (
                      <Dropdown.Item
                        text="Hide from gallery"
                        onClick={() => updateContentControlStatus(3, 'Hidden from public gallery')}
                      />
                    )}
                    <Dropdown.Item
                      text="Re-encode with current template"
                      disabled={loading || isUpdating}
                      onClick={() => setShowReencodeConfirmationModal(true)}
                    />
                    <Dropdown.Item text="Delete clip" onClick={() => handleDelete()} />
                  </Dropdown.Menu>
                </Dropdown>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Grid.Column>
      </Grid>

      <Confirm
        open={showReencodeConfirmationModal}
        header="Re-encode clip"
        content="Re-encode this clip using the project's current video template? The existing video will remain available while the new version is processed."
        confirmButton="Re-encode"
        onCancel={() => setShowReencodeConfirmationModal(false)}
        onConfirm={handleReencode}
      />

      <Confirm
        open={showConfirmationModal}
        header="Clip deletion"
        content="Are you sure you want to delete this clip?"
        confirmButton="Yes"
        onCancel={() => setShowConfirmationModal(false)}
        onConfirm={() => deleteClip(clip.id)}
      />
    </Modal>
  )
}

interface IProps {
  clip: IClip
  clipGroup: IClipGroup | null
  displaySetting: any
  loading: boolean
  updateClip: (arg: IClip, options: UpdateOptions) => void
  deleteClip: (arg: number) => void
  setShowModal: (arg: IClip | null) => void
}

export default GalleryClipModal
